// Route wrapper for the submission flow.
//
// It does what RouteWrapper does that this flow needs — capture UTM/tracking
// into the shared tData cookie on mount — but renders no global header bar, so
// the desktop hero can bleed to the top edge the way the Figma desktop section
// has it. The flow's layout draws the logo lockup itself at every width (the
// global bar only ever showed the icon on these routes).

import React, { useEffect, useLayoutEffect, useState } from "react";
import { Route, RouteProps } from "react-router-dom";
import styled from "styled-components";
import qs from "query-string";
import { setTrackingData } from "helpers";
import { ensureFlowLanguage } from "./Submission/common/flowLanguage";
import { carryStep4 } from "./Submission/common/step4Variant";

interface Props extends RouteProps {
  path: string;
  component: React.ComponentType<any>;
}

const SubmissionRoute: React.FC<Props> = ({ path, component: Component, ...rest }) => (
  <Route
    path={path}
    {...rest}
    render={(routeProps: any) => <Tracked Component={Component} {...routeProps} />}
  />
);

const Tracked: React.FC<any> = ({ Component, ...routeProps }) => {
  // A direct visit gets its language checked in App.tsx before anything
  // renders. Arriving from another page of the app, the language was set
  // there: switch to English if the flow isn't complete in it, and draw the
  // page again before it is painted (the pages call i18next.t directly, so
  // nothing else re-renders them).
  const [, setDraws] = useState(0);
  useLayoutEffect(() => {
    if (ensureFlowLanguage()) setDraws((n) => n + 1);
  }, []);

  useEffect(() => {
    // Same call RouteWrapper makes, so attribution behaves identically on
    // these routes (helpers/index.ts writes the 7-day parent-domain cookie).
    const params = qs.parse(routeProps.location.search);
    setTrackingData(params.src as string, routeProps.location.search);
    // Mount-only, exactly like RouteWrapper's own tracking effect. Re-running
    // it whenever location.search changes would overwrite the landing
    // attribution with whatever the current step's query string happens to be.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A step-4 arm picked by URL (?step4=a|b) stays on every flow URL from the
  // first page on, whichever hop dropped it, so the pick is visible and rides
  // along on every link that leaves the app.
  const { history, location } = routeProps;
  useEffect(() => {
    // The page's own effects run before this one, so a page that redirects on
    // mount (the checkout return page) has already moved on: carrying the pick
    // onto the old URL would take the visitor back to it. The page it moved to
    // gets the pick on its own mount.
    const now = history.location;
    if (now.pathname !== location.pathname || now.search !== location.search) return;
    const search = carryStep4(location.search);
    if (search !== null) {
      history.replace({ pathname: location.pathname, search, hash: location.hash, state: location.state });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return (
    <Body>
      <Component {...routeProps} />
    </Body>
  );
};

const Body = styled.div`
  display: flex;
`;

export default SubmissionRoute;
